import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Clock,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
} from "../components/ui/Dialog";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfDay,
  isPast,
} from "date-fns";
import { fetchClient } from "../lib/api-client";
import { toISOString } from "../lib/date-utils";
import { formatEventPrice } from "../lib/utils";
import type { Event } from "../lib/types";
import { EventScarcityBanner } from "../components/events/EventScarcityBanner";

const STALE_TIME_MS = 1000 * 60 * 5; // 5 minutes
const PAGE_SIZE = 100;
const MAX_PAGES = 50;

/** Fetch all pages of events for a calendar date range (shared for main query and prefetch). */
async function fetchEventsForCalendarRange(
  rangeStart: Date,
  rangeEnd: Date,
): Promise<Event[]> {
  const filter = `startsAt>=${JSON.stringify(toISOString(rangeStart))};startsAt<=${JSON.stringify(toISOString(rangeEnd))}`;
  const all: Event[] = [];
  const seenIds = new Set<string>();
  let page = 1;
  while (page <= MAX_PAGES) {
    const res = await fetchClient.GET("/api/public/events", {
      params: { query: { page, pageSize: PAGE_SIZE, filter } },
    });
    if (!res.data) {
      throw new Error(
        res.error && "message" in res.error
          ? String(res.error.message)
          : "Failed to fetch events",
      );
    }
    const pagination = res.data.pagination;
    const pageData = res.data.data as Event[];
    let newCount = 0;
    for (const event of pageData) {
      if (!seenIds.has(event.id)) {
        seenIds.add(event.id);
        all.push(event);
        newCount += 1;
      }
    }
    if (!pagination?.hasNext || (all.length > 0 && newCount === 0)) break;
    page += 1;
  }
  return all;
}

export const EventsCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const getEventPillClassName = (event: Event): string => {
    if (event.isCancelled) {
      return "bg-red-100 text-red-800 hover:bg-red-200";
    }
    if (!event.isBookable) {
      return "bg-gray-100 text-gray-800 hover:bg-gray-200";
    }
    return "bg-primary-100 text-primary-800 hover:bg-primary-200";
  };

  const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const monthEnd = useMemo(() => endOfMonth(currentDate), [currentDate]);
  const calendarStart = useMemo(
    () => startOfWeek(monthStart, { weekStartsOn: 0 }),
    [monthStart],
  );
  const calendarEnd = useMemo(
    () => endOfWeek(monthEnd, { weekStartsOn: 0 }),
    [monthEnd],
  );
  const monthDays = useMemo(
    () => eachDayOfInterval({ start: monthStart, end: monthEnd }),
    [monthStart, monthEnd],
  );
  const calendarDays = useMemo(
    () => eachDayOfInterval({ start: calendarStart, end: calendarEnd }),
    [calendarStart, calendarEnd],
  );

  // Stable query key so we don't refetch unnecessarily
  const calendarQueryKey = useMemo(
    () => ["events", "calendar", calendarStart.toISOString(), calendarEnd.toISOString()] as const,
    [calendarStart, calendarEnd],
  );

  // Fetch all pages of events for the visible range (API returns paginated results)
  const { data: events, isLoading, error } = useQuery({
    queryKey: calendarQueryKey,
    staleTime: STALE_TIME_MS,
    retry: 1,
    queryFn: () => fetchEventsForCalendarRange(calendarStart, calendarEnd),
  });

  const queryClient = useQueryClient();

  // Prefetch previous and next month when the selected month changes (instant nav with arrows)
  useEffect(() => {
    const prefetchMonth = (monthDate: Date) => {
      const start = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 });
      const end = endOfWeek(endOfMonth(monthDate), { weekStartsOn: 0 });
      const key = ["events", "calendar", start.toISOString(), end.toISOString()] as const;
      void queryClient.prefetchQuery({
        queryKey: key,
        queryFn: () => fetchEventsForCalendarRange(start, end),
        staleTime: STALE_TIME_MS,
      });
    };
    prefetchMonth(subMonths(currentDate, 1));
    prefetchMonth(addMonths(currentDate, 1));
  }, [currentDate, queryClient]);

  const eventsList = events ?? [];

  // Filter events to only show those starting in the current month
  const currentMonthEvents = useMemo(
    () =>
      eventsList.filter((event: Event) =>
        event.startsAt ? isSameMonth(new Date(event.startsAt), currentDate) : false,
      ),
    [eventsList, currentDate],
  );

  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    return eventsList.filter((event: Event) =>
      event.startsAt ? isSameDay(new Date(event.startsAt), date) : false,
    );
  };

  const getTagVariant = (tagName: string) => {
    const variants: Record<
      string,
      "default" | "success" | "warning" | "error" | "info"
    > = {
      Open: "success",
      Racing: "info",
      Training: "warning",
      Junior: "default",
      RYA: "error",
      Social: "default",
      Beginner: "info",
      Advanced: "warning",
    };
    return variants[tagName] || "default";
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Calendar</h1>
          <p className="text-gray-600 mt-1">View events by month</p>
        </div>
        <Card>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
            <span className="ml-3 text-gray-600">Loading events...</span>
          </div>
        </Card>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Calendar</h1>
          <p className="text-gray-600 mt-1">View events by month</p>
        </div>
        <Card>
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error loading events
            </h3>
            <p className="text-gray-600">
              An error occurred while loading events. Please try again later.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Calendar</h1>
          <p className="text-gray-600 mt-1">View events by month</p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentDate(subMonths(currentDate, 1))}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="text-xl font-semibold min-w-[200px] text-center">
            {format(currentDate, "MMMM yyyy")}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentDate(addMonths(currentDate, 1))}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Mobile Agenda View */}
      <Card className="md:hidden">
        <div className="p-4">
          {currentMonthEvents.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No events scheduled
              </h3>
              <p className="text-gray-600">
                There are no events scheduled for{" "}
                {format(currentDate, "MMMM yyyy")}.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {monthDays.map((day) => {
                const dayEvents = getEventsForDate(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isToday = isSameDay(day, new Date());
                const dayStart = startOfDay(day);
                const isPastDate = isPast(dayStart) && !isToday;

                if (!isCurrentMonth || dayEvents.length === 0) {
                  return null;
                }

                return (
                  <div key={day.toISOString()} className="space-y-3">
                    {/* Date Header */}
                    <div className="flex items-center gap-3">
                      <div
                        className={`
                        flex flex-col items-center justify-center min-w-[50px] py-1.5 px-2 rounded-lg
                        ${isToday ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-700"}
                        ${isPastDate ? "opacity-60" : ""}
                      `}
                      >
                        <div className="text-xs font-medium uppercase leading-tight">
                          {format(day, "EEE")}
                        </div>
                        <div className="text-lg font-bold leading-tight">
                          {format(day, "d")}
                        </div>
                      </div>
                      <div className="flex-1 border-b border-gray-200 pb-2">
                        <div
                          className={`
                          text-sm font-semibold
                          ${isToday ? "text-primary-600" : "text-gray-900"}
                        `}
                        >
                          {format(day, "EEEE, MMMM do")}
                        </div>
                      </div>
                    </div>

                    {/* Events */}
                    <div className="space-y-2">
                      {dayEvents.map((event) => (
                        <button
                          key={event.id}
                          onClick={() => setSelectedEvent(event)}
                          className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors active:bg-primary-100"
                        >
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="flex-1 min-w-0 font-semibold text-gray-900 text-sm leading-snug">
                                {event.title}
                              </h4>
                              <div className="flex shrink-0 flex-col items-end gap-1">
                                <div className="flex flex-wrap justify-end gap-2">
                                  {event.isCancelled && (
                                    <Badge variant="error" size="sm">
                                      Cancelled
                                    </Badge>
                                  )}
                                  {!event.isCancelled && (
                                    <EventScarcityBanner
                                      event={event}
                                      variant="badge"
                                    />
                                  )}
                                </div>
                                {!event.isCancelled && (
                                  <p className="text-sm font-semibold text-gray-900">
                                    {event.isBookable
                                      ? formatEventPrice(
                                          event.pricePoints,
                                          event.bookingClosesAt,
                                          event.startsAt,
                                        )
                                      : "Info"}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-1.5 text-xs text-gray-600">
                              <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span>
                                {event.startsAt && format(new Date(event.startsAt), "h:mm a")}
                                {event.startsAt && event.endsAt && isSameDay(
                                  new Date(event.startsAt),
                                  new Date(event.endsAt),
                                ) && (
                                  <>
                                    {" "}
                                    - {format(new Date(event.endsAt), "h:mm a")}
                                  </>
                                )}
                              </span>
                            </div>

                            {event.location && (
                              <p className="text-xs text-gray-500">
                                {event.location}
                              </p>
                            )}

                            {(event.tags as string[])?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 pt-1">
                                {(event.tags as string[])
                                  ?.slice(0, 2)
                                  .map((tagName) => (
                                    <Badge
                                      key={tagName}
                                      variant={getTagVariant(tagName)}
                                      size="sm"
                                    >
                                      {tagName}
                                    </Badge>
                                  ))}
                                {(event.tags as string[])?.length > 2 && (
                                  <span className="text-xs text-gray-500">
                                    +{(event.tags as string[])?.length - 2}{" "}
                                    more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Calendar Grid - Hidden on mobile, shown on md and up */}
      <Card className="hidden md:block">
        <div className="p-6">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                className="text-center text-sm font-medium text-gray-500 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day) => {
              const dayEvents = getEventsForDate(day);
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isToday = isSameDay(day, new Date());

              return (
                <div
                  key={day.toISOString()}
                  className={`
                    min-h-[120px] p-2 border border-gray-200 rounded
                    ${isCurrentMonth ? "bg-white" : "bg-gray-50"}
                    ${isToday ? "ring-2 ring-primary-500" : ""}
                  `}
                >
                  <div
                    className={`
                    text-sm font-medium mb-1
                    ${isCurrentMonth ? "text-gray-900" : "text-gray-400"}
                    ${isToday ? "text-primary-600" : ""}
                  `}
                  >
                    {format(day, "d")}
                  </div>

                  <div className="space-y-1">
                    {dayEvents.slice(0, 2).map((event) => (
                      <button
                        key={event.id}
                        onClick={() => setSelectedEvent(event)}
                        className={`w-full text-left text-xs p-1 rounded truncate transition-colors cursor-pointer ${getEventPillClassName(event)}`}
                      >
                        {event.title}
                      </button>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-xs text-gray-500">
                        +{dayEvents.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Events List for Current Month - Hidden on mobile, shown on md and up */}
      <Card className="hidden md:block">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">
            Events in {format(currentDate, "MMMM yyyy")}
          </h3>

          {currentMonthEvents.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                No events scheduled for this month
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {currentMonthEvents.map((event: Event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h4 className="font-medium text-gray-900 min-w-0 flex-1">
                        {event.title}
                      </h4>
                      <div className="flex shrink-0 flex-wrap gap-2 justify-end">
                        {event.isCancelled && (
                          <Badge variant="error" size="sm">
                            Cancelled
                          </Badge>
                        )}
                        {!event.isCancelled && (
                          <EventScarcityBanner
                            event={event}
                            variant="badge"
                          />
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {event.startsAt && format(
                        new Date(event.startsAt),
                        "EEEE, MMMM do, yyyy • h:mm a",
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      {(event.tags as string[])?.map((tagName) => (
                        <Badge
                          key={tagName}
                          variant={getTagVariant(tagName)}
                          size="sm"
                        >
                          {tagName}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  {event.isBookable && (
                    <div className="text-right">
                      {!event.isCancelled && (
                        <p className="font-semibold">
                          {formatEventPrice(
                            event.pricePoints,
                            event.bookingClosesAt,
                            event.startsAt,
                          )}
                        </p>
                      )}
                      <Button
                        size="sm"
                        className={event.isCancelled ? "" : "mt-2"}
                        asChild
                      >
                        <Link to={`/events/${event.id}`}>View Details</Link>
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Event Details Dialog */}
      <Dialog open={!!selectedEvent} onClose={() => setSelectedEvent(null)}>
        {selectedEvent && (
          <>
            <DialogHeader onClose={() => setSelectedEvent(null)}>
              <DialogTitle>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <span className="min-w-0 flex-1 font-semibold text-left">
                    {selectedEvent.title}
                  </span>
                  <div className="flex shrink-0 flex-wrap justify-end gap-2">
                    {selectedEvent.isCancelled && (
                      <Badge variant="error" size="sm">
                        Cancelled
                      </Badge>
                    )}
                    {!selectedEvent.isCancelled && (
                      <EventScarcityBanner
                        event={selectedEvent}
                        variant="badge"
                      />
                    )}
                  </div>
                </div>
              </DialogTitle>
            </DialogHeader>
            <DialogContent>
              <div className="space-y-4">
                <div className="flex items-start gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    {selectedEvent.startsAt && format(
                      new Date(selectedEvent.startsAt),
                      "EEEE, MMMM do, yyyy",
                    )}
                  </span>
                </div>

                <div className="flex items-start gap-2 text-sm">
                  <Clock className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    {selectedEvent.startsAt && format(new Date(selectedEvent.startsAt), "h:mm a")}
                    {selectedEvent.startsAt && selectedEvent.endsAt && (
                      <>
                        {" - "}
                        {format(new Date(selectedEvent.endsAt), "h:mm a")}
                      </>
                    )}
                  </span>
                </div>

                {selectedEvent.description && (
                  <p className="text-sm text-gray-600">
                    {selectedEvent.description}
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  {(selectedEvent.tags as string[])?.map((tagName) => (
                    <Badge
                      key={tagName}
                      variant={getTagVariant(tagName)}
                      size="sm"
                    >
                      {tagName}
                    </Badge>
                  ))}
                </div>

                {selectedEvent.isBookable && !selectedEvent.isCancelled && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-lg font-semibold text-gray-900">
                      {formatEventPrice(
                        selectedEvent.pricePoints,
                        selectedEvent.bookingClosesAt,
                        selectedEvent.startsAt,
                      )}
                    </p>
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedEvent(null)}>
                Close
              </Button>
              {selectedEvent.isBookable && <Button asChild>
                <Link to={`/events/${selectedEvent.id}`}>
                  View Full Details
                </Link>
              </Button>}
            </DialogFooter>
          </>
        )}
      </Dialog>
    </div>
  );
};
